import { Controller } from "@hotwired/stimulus"
import { computePosition, offset, shift, flip, size } from "@floating-ui/dom"
import { reactor } from "../lib/reactor"

export default class extends Controller {
  static targets = ["content"]
  static values = { placement: { type: String, default: "bottom" } }

  connect() {
    reactor(this)
    this.visible = false
    this.handleViewportChange = () => this.visible && this.reposition()
    window.addEventListener("resize", this.handleViewportChange)
    window.visualViewport?.addEventListener("resize", this.handleViewportChange)
    window.visualViewport?.addEventListener("scroll", this.handleViewportChange)

    if (this.element.tabIndex < 0) {
      this.element.tabIndex = 0
    }
    this.describedBy = this.element.getAttribute("aria-describedby")

    this.showHandler = () => this.show()
    this.hideHandler = () => this.hide()
    this.keyHandler = (event) => {
      if (event.key === "Escape") this.hide()
    }
    this.scrollHandler = (event) => {
      if (this.#insideTooltip(event.target)) return
      this.#dismissFromGesture()
    }
    this.touchStartHandler = (event) => {
      if (!this.visible || event.touches.length !== 1) return
      const touch = event.touches[0]
      this.touchOrigin = { x: touch.clientX, y: touch.clientY }
      this.touchInside = this.#pointInside(touch.clientX, touch.clientY)
    }
    this.touchMoveHandler = (event) => {
      if (!this.visible || !this.touchOrigin || this.touchInside || event.touches.length !== 1) return
      const touch = event.touches[0]
      const dx = touch.clientX - this.touchOrigin.x
      const dy = touch.clientY - this.touchOrigin.y
      if (dx * dx + dy * dy >= 64) this.#dismissFromGesture()
    }

    this.element.addEventListener("mouseenter", this.showHandler)
    this.element.addEventListener("mouseleave", this.hideHandler)
    this.element.addEventListener("focus", this.showHandler)
    this.element.addEventListener("blur", this.hideHandler)
    this.element.addEventListener("keydown", this.keyHandler)
    window.addEventListener("scroll", this.scrollHandler, true)
    window.addEventListener("touchstart", this.touchStartHandler, { passive: true, capture: true })
    window.addEventListener("touchmove", this.touchMoveHandler, { passive: true, capture: true })
  }

  disconnect() {
    window.removeEventListener("resize", this.handleViewportChange)
    window.visualViewport?.removeEventListener("resize", this.handleViewportChange)
    window.visualViewport?.removeEventListener("scroll", this.handleViewportChange)
    this.element.removeEventListener("mouseenter", this.showHandler)
    this.element.removeEventListener("mouseleave", this.hideHandler)
    this.element.removeEventListener("focus", this.showHandler)
    this.element.removeEventListener("blur", this.hideHandler)
    this.element.removeEventListener("keydown", this.keyHandler)
    window.removeEventListener("scroll", this.scrollHandler, true)
    window.removeEventListener("touchstart", this.touchStartHandler, true)
    window.removeEventListener("touchmove", this.touchMoveHandler, true)
    this.tooltip?.remove()
    this.#restoreDescribedBy()
  }

  morph() {
    if (!this.visible) return

    if (!this.tooltip) return this.show()

    this.tooltip.innerHTML = this.contentTarget.innerHTML
    if (!this.tooltip.isConnected) document.body.appendChild(this.tooltip)
    this.reposition()
  }

  show() {
    this.visible = true
    this.tooltip?.remove()
    this.tooltip = this.createTooltip()

    Object.assign(this.tooltip.style, {
      position: "fixed",
      maxWidth: "min(24rem, calc(100vw - 2rem))",
      visibility: "hidden",
      left: "0",
      top: "0"
    })
    document.body.appendChild(this.tooltip)
    this.element.setAttribute("aria-describedby", this.tooltip.id)

    this.reposition().then(() => {
      requestAnimationFrame(() => this.tooltip?.classList.remove("opacity-0", "translate-y-2"))
    })
  }

  reposition() {
    if (!this.tooltip?.isConnected) return Promise.resolve()

    const padding = this.#framePadding()
    return computePosition(this.element, this.tooltip, {
      placement: this.placementValue,
      strategy: "fixed",
      middleware: [
        offset(20),
        flip({ padding }),
        shift({ padding }),
        size({
          padding,
          apply: ({ availableHeight, elements }) => {
            elements.floating.style.maxHeight = `${availableHeight}px`
          }
        })
      ]
    }).then(({ x, y }) => {
      if (!this.tooltip) return
      const viewportTop = window.visualViewport?.offsetTop ?? 0
      const viewportBottom = viewportTop + (window.visualViewport?.height ?? window.innerHeight)
      const bottomBar = document.getElementById("mobile-bottom-bar")
      const bottomBarTop = bottomBar?.getBoundingClientRect().top
      const hasBottomBar = bottomBar && bottomBarTop < viewportBottom && bottomBarTop > viewportTop
      const panelBottom = hasBottomBar ? bottomBarTop - 8 : viewportBottom - 8
      const panelTop = Math.max(viewportTop + 8, y)
      const maxHeight = Math.max(8, Math.floor(panelBottom - panelTop - 8))
      // scrollHeight leaves out the border, so a panel sized to it scrolls by 2px.
      const border = this.tooltip.offsetHeight - this.tooltip.clientHeight
      const finalHeight = Math.min(this.tooltip.scrollHeight + border, maxHeight)

      Object.assign(this.tooltip.style, {
        left: `${x}px`,
        top: `${panelTop}px`,
        height: `${finalHeight}px`,
        maxHeight: `${maxHeight}px`,
        overflowY: "auto",
        visibility: "visible"
      })
    })
  }

  hide() {
    this.visible = false
    this.touchOrigin = null
    this.#restoreDescribedBy()
    const tooltip = this.tooltip
    this.tooltip = null
    if (!tooltip) return

    tooltip.classList.add("opacity-0", "translate-y-2")
    tooltip.addEventListener("transitionend", () => tooltip.remove(), { once: true })
  }

  #dismissFromGesture() {
    if (!this.visible) return
    const trigger = this.element
    this.hide()
    if (document.activeElement === trigger) trigger.blur()
  }

  #framePadding() {
    const bar = document.getElementById("mobile-bottom-bar")
    const barHeight = bar && bar.getClientRects().length ? bar.offsetHeight : 0
    return { top: 8, right: 8, bottom: barHeight + 8, left: 8 }
  }

  #insideTooltip(target) {
    return !!this.tooltip && (target === this.tooltip || this.tooltip.contains(target))
  }

  #pointInside(x, y) {
    const rect = this.tooltip?.getBoundingClientRect()
    if (!rect) return false
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  #restoreDescribedBy() {
    if (this.describedBy) {
      this.element.setAttribute("aria-describedby", this.describedBy)
    } else {
      this.element.removeAttribute("aria-describedby")
    }
  }

  createTooltip() {
    const tooltip = document.createElement("div")
    tooltip.id = `tooltip-${this.element.id || crypto.randomUUID()}`
    tooltip.setAttribute("role", "tooltip")
    tooltip.innerHTML = this.contentTarget.innerHTML
    Object.assign(tooltip.style, {
      position: "fixed",
      maxWidth: "min(24rem, calc(100vw - 2rem))",
      overflowY: "auto",
      overscrollBehavior: "contain",
      scrollbarWidth: "thin"
    })
    tooltip.classList.add(
      "tooltip-panel",
      "absolute",
      "p-5",
      "bg-white",
      "rounded-xl",
      "border",
      "border-gray-300",
      "shadow-xl",
      "text-sm",
      "leading-relaxed",
      "text-gray-700",
      "dark:shadow-gray-900",
      "dark:bg-gray-900",
      "dark:border-gray-750",
      "dark:text-gray-on-gray-900",
      "transition",
      "duration-150",
      "ease-out",
      "opacity-0",
      "translate-y-2"
    )

    return tooltip
  }
}
